import { type ContractRouterClient, oc } from "@orpc/contract";
import * as admin from "./schemas/admin";

// =============
// Admin Apps Contract
// =============
export const adminAppsContract = {
	list: oc
		.route({
			method: "GET",
			path: "/apps",
			summary: "List all apps",
			description:
				"Returns a paginated list of all apps in the system. Supports filtering, sorting, and including soft-deleted records.",
			tags: ["Admin - Apps"],
		})
		.input(admin.AdminAppListInputSchema)
		.output(admin.AdminAppListOutputSchema),

	getOne: oc
		.route({
			method: "GET",
			path: "/apps/{id}",
			summary: "Get app by ID",
			description: "Returns a single app by its ID.",
			tags: ["Admin - Apps"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminAppOutputSchema),

	getMany: oc
		.route({
			method: "GET",
			path: "/apps/batch",
			summary: "Get multiple apps by IDs",
			description:
				"Returns multiple apps by their IDs. Used for React Admin reference fields.",
			tags: ["Admin - Apps"],
		})
		.input(admin.AdminGetManyInputSchema)
		.output(admin.AdminAppListOutputSchema),

	create: oc
		.route({
			method: "POST",
			path: "/apps",
			summary: "Create a new app",
			description: "Creates a new app with the specified owner.",
			tags: ["Admin - Apps"],
			successStatus: 201,
		})
		.input(admin.AdminAppCreateInputSchema)
		.output(admin.AdminAppOutputSchema),

	update: oc
		.route({
			method: "PUT",
			path: "/apps/{id}",
			summary: "Update an app",
			description: "Updates an existing app by ID.",
			tags: ["Admin - Apps"],
		})
		.input(admin.AdminAppUpdateInputSchema)
		.output(admin.AdminAppOutputSchema),

	delete: oc
		.route({
			method: "DELETE",
			path: "/apps/{id}",
			summary: "Soft delete an app",
			description:
				"Soft deletes an app by setting deletedAt. Can be restored later.",
			tags: ["Admin - Apps"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminAppOutputSchema),

	restore: oc
		.route({
			method: "POST",
			path: "/apps/{id}/restore",
			summary: "Restore a deleted app",
			description: "Restores a soft-deleted app by clearing deletedAt.",
			tags: ["Admin - Apps"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminAppOutputSchema),

	deleteMany: oc
		.route({
			method: "DELETE",
			path: "/apps/batch",
			summary: "Soft delete multiple apps",
			description: "Soft deletes multiple apps by their IDs.",
			tags: ["Admin - Apps"],
		})
		.input(admin.AdminDeleteManyInputSchema)
		.output(admin.AdminDeleteManyOutputSchema),
};

// =============
// Admin Partners Contract
// =============
export const adminPartnersContract = {
	list: oc
		.route({
			method: "GET",
			path: "/partners",
			summary: "List all partners",
			description:
				"Returns a paginated list of all partners. Supports filtering and sorting.",
			tags: ["Admin - Partners"],
		})
		.input(admin.AdminPartnerListInputSchema)
		.output(admin.AdminPartnerListOutputSchema),

	getOne: oc
		.route({
			method: "GET",
			path: "/partners/{id}",
			summary: "Get partner by ID",
			description: "Returns a single partner by its ID.",
			tags: ["Admin - Partners"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminPartnerOutputSchema),

	getMany: oc
		.route({
			method: "GET",
			path: "/partners/batch",
			summary: "Get multiple partners by IDs",
			description: "Returns multiple partners by their IDs.",
			tags: ["Admin - Partners"],
		})
		.input(admin.AdminGetManyInputSchema)
		.output(admin.AdminPartnerListOutputSchema),

	create: oc
		.route({
			method: "POST",
			path: "/partners",
			summary: "Create a new partner",
			description: "Creates a new partner with the specified owner.",
			tags: ["Admin - Partners"],
			successStatus: 201,
		})
		.input(admin.AdminPartnerCreateInputSchema)
		.output(admin.AdminPartnerOutputSchema),

	update: oc
		.route({
			method: "PUT",
			path: "/partners/{id}",
			summary: "Update a partner",
			description: "Updates an existing partner by ID.",
			tags: ["Admin - Partners"],
		})
		.input(admin.AdminPartnerUpdateInputSchema)
		.output(admin.AdminPartnerOutputSchema),

	delete: oc
		.route({
			method: "DELETE",
			path: "/partners/{id}",
			summary: "Soft delete a partner",
			description:
				"Soft deletes a partner by setting deletedAt. Includes deactivation reason to distinguish cleanup from fraud/abuse.",
			tags: ["Admin - Partners"],
		})
		.input(admin.AdminPartnerDeleteInputSchema)
		.output(admin.AdminPartnerOutputSchema),

	restore: oc
		.route({
			method: "POST",
			path: "/partners/{id}/restore",
			summary: "Restore a deleted partner",
			description: "Restores a soft-deleted partner.",
			tags: ["Admin - Partners"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminPartnerOutputSchema),

	deleteMany: oc
		.route({
			method: "DELETE",
			path: "/partners/batch",
			summary: "Soft delete multiple partners",
			description: "Soft deletes multiple partners by their IDs.",
			tags: ["Admin - Partners"],
		})
		.input(admin.AdminDeleteManyInputSchema)
		.output(admin.AdminDeleteManyOutputSchema),
};

// =============
// Admin Referral Links Contract
// =============
export const adminReferralLinksContract = {
	list: oc
		.route({
			method: "GET",
			path: "/referral-links",
			summary: "List all referral links",
			description:
				"Returns a paginated list of all referral links. Supports filtering and sorting.",
			tags: ["Admin - Referral Links"],
		})
		.input(admin.AdminReferralLinkListInputSchema)
		.output(admin.AdminReferralLinkListOutputSchema),

	getOne: oc
		.route({
			method: "GET",
			path: "/referral-links/{id}",
			summary: "Get referral link by ID",
			description: "Returns a single referral link by its ID.",
			tags: ["Admin - Referral Links"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminReferralLinkOutputSchema),

	getMany: oc
		.route({
			method: "GET",
			path: "/referral-links/batch",
			summary: "Get multiple referral links by IDs",
			description: "Returns multiple referral links by their IDs.",
			tags: ["Admin - Referral Links"],
		})
		.input(admin.AdminGetManyInputSchema)
		.output(admin.AdminReferralLinkListOutputSchema),

	create: oc
		.route({
			method: "POST",
			path: "/referral-links",
			summary: "Create a new referral link",
			description: "Creates a new referral link.",
			tags: ["Admin - Referral Links"],
			successStatus: 201,
		})
		.input(admin.AdminReferralLinkCreateInputSchema)
		.output(admin.AdminReferralLinkOutputSchema),

	update: oc
		.route({
			method: "PUT",
			path: "/referral-links/{id}",
			summary: "Update a referral link",
			description: "Updates an existing referral link by ID.",
			tags: ["Admin - Referral Links"],
		})
		.input(admin.AdminReferralLinkUpdateInputSchema)
		.output(admin.AdminReferralLinkOutputSchema),

	delete: oc
		.route({
			method: "DELETE",
			path: "/referral-links/{id}",
			summary: "Soft delete a referral link",
			description: "Soft deletes a referral link by setting deletedAt.",
			tags: ["Admin - Referral Links"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminReferralLinkOutputSchema),

	restore: oc
		.route({
			method: "POST",
			path: "/referral-links/{id}/restore",
			summary: "Restore a deleted referral link",
			description: "Restores a soft-deleted referral link.",
			tags: ["Admin - Referral Links"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminReferralLinkOutputSchema),

	deleteMany: oc
		.route({
			method: "DELETE",
			path: "/referral-links/batch",
			summary: "Soft delete multiple referral links",
			description: "Soft deletes multiple referral links by their IDs.",
			tags: ["Admin - Referral Links"],
		})
		.input(admin.AdminDeleteManyInputSchema)
		.output(admin.AdminDeleteManyOutputSchema),
};

// =============
// Admin Users Contract
// =============
export const adminUsersContract = {
	list: oc
		.route({
			method: "GET",
			path: "/users",
			summary: "List all users",
			description:
				"Returns a paginated list of all users. Supports filtering and sorting.",
			tags: ["Admin - Users"],
		})
		.input(admin.AdminUserListInputSchema)
		.output(admin.AdminUserListOutputSchema),

	getOne: oc
		.route({
			method: "GET",
			path: "/users/{id}",
			summary: "Get user by ID",
			description: "Returns a single user by their ID.",
			tags: ["Admin - Users"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminUserOutputSchema),

	getMany: oc
		.route({
			method: "GET",
			path: "/users/batch",
			summary: "Get multiple users by IDs",
			description: "Returns multiple users by their IDs.",
			tags: ["Admin - Users"],
		})
		.input(admin.AdminGetManyInputSchema)
		.output(admin.AdminUserListOutputSchema),

	create: oc
		.route({
			method: "POST",
			path: "/users",
			summary: "Create a new user",
			description: "Creates a new user account.",
			tags: ["Admin - Users"],
			successStatus: 201,
		})
		.input(admin.AdminUserCreateInputSchema)
		.output(admin.AdminUserOutputSchema),

	update: oc
		.route({
			method: "PUT",
			path: "/users/{id}",
			summary: "Update a user",
			description: "Updates an existing user by ID.",
			tags: ["Admin - Users"],
		})
		.input(admin.AdminUserUpdateInputSchema)
		.output(admin.AdminUserOutputSchema),

	delete: oc
		.route({
			method: "DELETE",
			path: "/users/{id}",
			summary: "Soft delete a user",
			description: "Soft deletes a user by setting deletedAt.",
			tags: ["Admin - Users"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminUserOutputSchema),

	restore: oc
		.route({
			method: "POST",
			path: "/users/{id}/restore",
			summary: "Restore a deleted user",
			description: "Restores a soft-deleted user.",
			tags: ["Admin - Users"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminUserOutputSchema),

	deleteMany: oc
		.route({
			method: "DELETE",
			path: "/users/batch",
			summary: "Soft delete multiple users",
			description: "Soft deletes multiple users by their IDs.",
			tags: ["Admin - Users"],
		})
		.input(admin.AdminDeleteManyInputSchema)
		.output(admin.AdminDeleteManyOutputSchema),

	// User-specific actions
	ban: oc
		.route({
			method: "POST",
			path: "/users/{id}/ban",
			summary: "Ban a user",
			description:
				"Bans a user with optional reason and expiration. Requires admin role.",
			tags: ["Admin - Users"],
		})
		.input(admin.AdminUserBanInputSchema)
		.output(admin.AdminUserOutputSchema),

	unban: oc
		.route({
			method: "POST",
			path: "/users/{id}/unban",
			summary: "Unban a user",
			description: "Removes the ban from a user. Requires admin role.",
			tags: ["Admin - Users"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminUserOutputSchema),

	// Super admin only
	updateRole: oc
		.route({
			method: "POST",
			path: "/users/{id}/role",
			summary: "Update user role",
			description:
				"Changes a user's role (user, admin, super_admin). Requires super_admin role.",
			tags: ["Admin - Users"],
		})
		.input(admin.AdminUserRoleUpdateInputSchema)
		.output(admin.AdminUserOutputSchema),
};

// =============
// Admin Audit Logs Contract (Read-only)
// =============
export const adminAuditLogsContract = {
	list: oc
		.route({
			method: "GET",
			path: "/audit-logs",
			summary: "List audit logs",
			description:
				"Returns a paginated list of admin audit logs. Supports filtering by actor, action, resource type, and date range.",
			tags: ["Admin - Audit Logs"],
		})
		.input(admin.AdminAuditLogListInputSchema)
		.output(admin.AdminAuditLogListOutputSchema),
};

// =============
// Admin Purchase Events Contract (Read-only)
// =============
export const adminPurchaseEventsContract = {
	list: oc
		.route({
			method: "GET",
			path: "/purchase-events",
			summary: "List purchase events",
			description:
				"Returns a paginated list of all purchase events. Supports filtering by app, status, and date range.",
			tags: ["Admin - Purchase Events"],
		})
		.input(admin.AdminPurchaseEventListInputSchema)
		.output(admin.AdminPurchaseEventListOutputSchema),
};

// =============
// Admin Dead Letter Events Contract (Read-only)
// =============
export const adminDeadLetterEventsContract = {
	list: oc
		.route({
			method: "GET",
			path: "/dead-letter-events",
			summary: "List dead letter events",
			description:
				"Returns a paginated list of dead letter events. Supports filtering by source and resolution status.",
			tags: ["Admin - Dead Letter Events"],
		})
		.input(admin.AdminDeadLetterEventListInputSchema)
		.output(admin.AdminDeadLetterEventListOutputSchema),
};

// =============
// Admin Email DLQ Contract (R2 Storage)
// =============
export const adminEmailDlqContract = {
	list: oc
		.route({
			method: "GET",
			path: "/email-dlq",
			summary: "List email DLQ records",
			description:
				"Returns failed email records from R2 storage. Supports filtering by recipient type and date.",
			tags: ["Admin - Email DLQ"],
		})
		.input(admin.AdminEmailDlqListInputSchema)
		.output(admin.AdminEmailDlqListOutputSchema),

	replay: oc
		.route({
			method: "POST",
			path: "/email-dlq/replay",
			summary: "Replay a failed email",
			description:
				"Re-enqueues a failed email for sending. Creates a replay marker (append-only).",
			tags: ["Admin - Email DLQ"],
		})
		.input(admin.AdminEmailDlqReplayInputSchema)
		.output(admin.AdminEmailDlqReplayOutputSchema),
};

// =============
// Admin Partner Earnings Contract
// =============
export const adminPartnerEarningsContract = {
	list: oc
		.route({
			method: "GET",
			path: "/partner-earnings",
			summary: "List all partner earnings",
			description:
				"Returns a paginated list of all partner earnings. Supports filtering and sorting.",
			tags: ["Admin - Partner Earnings"],
		})
		.input(admin.AdminPartnerEarningListInputSchema)
		.output(admin.AdminPartnerEarningListOutputSchema),

	getOne: oc
		.route({
			method: "GET",
			path: "/partner-earnings/{id}",
			summary: "Get partner earning by ID",
			description: "Returns a single partner earning by its ID.",
			tags: ["Admin - Partner Earnings"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminPartnerEarningOutputSchema),

	getMany: oc
		.route({
			method: "GET",
			path: "/partner-earnings/batch",
			summary: "Get multiple partner earnings by IDs",
			description: "Returns multiple partner earnings by their IDs.",
			tags: ["Admin - Partner Earnings"],
		})
		.input(admin.AdminGetManyInputSchema)
		.output(admin.AdminPartnerEarningListOutputSchema),

	create: oc
		.route({
			method: "POST",
			path: "/partner-earnings",
			summary: "Create a new partner earning",
			description: "Creates a new partner earning record.",
			tags: ["Admin - Partner Earnings"],
			successStatus: 201,
		})
		.input(admin.AdminPartnerEarningCreateInputSchema)
		.output(admin.AdminPartnerEarningOutputSchema),

	update: oc
		.route({
			method: "PUT",
			path: "/partner-earnings/{id}",
			summary: "Update a partner earning",
			description: "Updates an existing partner earning by ID.",
			tags: ["Admin - Partner Earnings"],
		})
		.input(admin.AdminPartnerEarningUpdateInputSchema)
		.output(admin.AdminPartnerEarningOutputSchema),

	delete: oc
		.route({
			method: "DELETE",
			path: "/partner-earnings/{id}",
			summary: "Delete a partner earning",
			description: "Deletes a partner earning record.",
			tags: ["Admin - Partner Earnings"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminPartnerEarningOutputSchema),

	deleteMany: oc
		.route({
			method: "DELETE",
			path: "/partner-earnings/batch",
			summary: "Delete multiple partner earnings",
			description: "Deletes multiple partner earnings by their IDs.",
			tags: ["Admin - Partner Earnings"],
		})
		.input(admin.AdminDeleteManyInputSchema)
		.output(admin.AdminDeleteManyOutputSchema),
};

// =============
// Admin Partner Payouts Contract
// =============
export const adminPartnerPayoutsContract = {
	list: oc
		.route({
			method: "GET",
			path: "/partner-payouts",
			summary: "List all partner payouts",
			description:
				"Returns a paginated list of all partner payouts. Supports filtering and sorting.",
			tags: ["Admin - Partner Payouts"],
		})
		.input(admin.AdminPartnerPayoutListInputSchema)
		.output(admin.AdminPartnerPayoutListOutputSchema),

	getOne: oc
		.route({
			method: "GET",
			path: "/partner-payouts/{id}",
			summary: "Get partner payout by ID",
			description: "Returns a single partner payout by its ID.",
			tags: ["Admin - Partner Payouts"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminPartnerPayoutOutputSchema),

	getMany: oc
		.route({
			method: "GET",
			path: "/partner-payouts/batch",
			summary: "Get multiple partner payouts by IDs",
			description: "Returns multiple partner payouts by their IDs.",
			tags: ["Admin - Partner Payouts"],
		})
		.input(admin.AdminGetManyInputSchema)
		.output(admin.AdminPartnerPayoutListOutputSchema),

	create: oc
		.route({
			method: "POST",
			path: "/partner-payouts",
			summary: "Create a new partner payout",
			description: "Creates a new partner payout record.",
			tags: ["Admin - Partner Payouts"],
			successStatus: 201,
		})
		.input(admin.AdminPartnerPayoutCreateInputSchema)
		.output(admin.AdminPartnerPayoutOutputSchema),

	update: oc
		.route({
			method: "PUT",
			path: "/partner-payouts/{id}",
			summary: "Update a partner payout",
			description: "Updates an existing partner payout by ID.",
			tags: ["Admin - Partner Payouts"],
		})
		.input(admin.AdminPartnerPayoutUpdateInputSchema)
		.output(admin.AdminPartnerPayoutOutputSchema),

	delete: oc
		.route({
			method: "DELETE",
			path: "/partner-payouts/{id}",
			summary: "Delete a partner payout",
			description: "Deletes a partner payout record.",
			tags: ["Admin - Partner Payouts"],
		})
		.input(admin.AdminIdInputSchema)
		.output(admin.AdminPartnerPayoutOutputSchema),

	deleteMany: oc
		.route({
			method: "DELETE",
			path: "/partner-payouts/batch",
			summary: "Delete multiple partner payouts",
			description: "Deletes multiple partner payouts by their IDs.",
			tags: ["Admin - Partner Payouts"],
		})
		.input(admin.AdminDeleteManyInputSchema)
		.output(admin.AdminDeleteManyOutputSchema),
};

// =============
// Combined Admin Router Contract
// =============
export const adminRouterContract = {
	apps: adminAppsContract,
	partners: adminPartnersContract,
	referralLinks: adminReferralLinksContract,
	users: adminUsersContract,
	auditLogs: adminAuditLogsContract,
	purchaseEvents: adminPurchaseEventsContract,
	deadLetterEvents: adminDeadLetterEventsContract,
	emailDlq: adminEmailDlqContract,
	partnerEarnings: adminPartnerEarningsContract,
	partnerPayouts: adminPartnerPayoutsContract,
};

export type AdminRouterContract = ContractRouterClient<
	typeof adminRouterContract
>;
